import { KeyboardArrowDown, KeyboardArrowUp } from "@material-ui/icons";
import { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { InputField, PasswordField } from "../../../common/fields";
import { deleteData } from "../../../common/storage";
import { WizardContext } from "../../../common/wizard/context";
import { setDisableNextControl } from "../../../common/wizard/reducer/actions";
import { useFormValidation } from "../../../shared/hooks/useFormValidation";
import { useSearchParams } from "../../../shared/hooks/useSearchParams";
import { asyncRegister } from "../../actions/onboardingActions";
import { steps } from "../../steps";
import { createUserStyles } from "./createUser.styles";

const errorsForm = {
  firstName: {
    isValid: true,
    error: "",
    value: "",
    isDirty: false,
    validators: { isRequired: true },
  },
  lastName: {
    isValid: true,
    error: "",
    value: "",
    isDirty: false,
    validators: { isRequired: true },
  },
  emailAddress: {
    isValid: true,
    error: "",
    value: "",
    isDirty: false,
    validators: { isRequired: true, isValidEmail: true },
  },
  password: {
    isValid: true,
    error: "",
    value: "",
    isDirty: false,
    validators: { isRequired: true, isPassword: true },
  },
  promotionCode: {
    isValid: true,
    error: "",
    value: "",
    isDirty: false,
  },
};

const defaultFocusId = 1;

export const CreateUser = () => {
  const classes = createUserStyles();
  const searchParams = useSearchParams();
  const {
    onHandleSubmit,
    dispatch: dispatchWizard,
    setTitle,
    setPercent,
  } = useContext(WizardContext);
  const [promotionCodeOpen, setPromotionCodeOpen] = useState(false);
  const [formGroup, setFormGroup] = useState(errorsForm);
  const dispatch = useDispatch();
  const { validateForm, resetValidation } = useFormValidation(
    formGroup,
    setFormGroup
  );

  const { WANT_TO_ACHIVE, user: participantProfile } = useSelector(
    (state) => state.onboarding.onboarding
  );

  const { request } = useSelector((state) => state.request);

  useEffect(() => {
    setTitle(steps[1].title);
    setPercent(100);
  }, []);

  useEffect(() => {
    if (participantProfile.profileId) {
      const { firstName, lastName, email } = participantProfile;

      setFormGroup({
        ...formGroup,
        firstName: {
          ...formGroup.firstName,
          value: firstName,
        },
        lastName: {
          ...formGroup.lastName,
          value: lastName,
        },
        emailAddress: {
          ...formGroup.emailAddress,
          value: email,
        },
      });
    }
  }, [participantProfile?.profileId]);

  const isValidForm = () =>
    formGroup.lastName.isValid &&
    formGroup.lastName.value !== "" &&
    formGroup.emailAddress.isValid &&
    formGroup.emailAddress.value !== "" &&
    formGroup.password.isValid &&
    formGroup.password.value !== "" &&
    formGroup.firstName.isValid &&
    formGroup.firstName.value !== "";

  useEffect(() => {
    dispatchWizard(setDisableNextControl(!isValidForm()));
  }, [formGroup]);

  useEffect(() => {
    if (request.type === "success") {
      setFormGroup(errorsForm);
      deleteData();
    } else if (request.type === "error") {
      setFormGroup(errorsForm);
    }
  }, [request]);

  const handleFormChange = (event) => {
    validateForm(event);
  };

  const handleTogglePromotion = () => setPromotionCodeOpen((value) => !value);

  onHandleSubmit(() => {
    const participantProfileId = participantProfile.profileId
      ? { participantProfileId: participantProfile.profileId }
      : undefined;
    const [answer] = WANT_TO_ACHIVE.answers;

    if (isValidForm()) {
      dispatch(
        asyncRegister(
          {
            ...participantProfileId,
            name: formGroup.firstName.value,
            lastName: formGroup.lastName.value,
            email: formGroup.emailAddress.value,
            password: formGroup.password.value,
            promotionCode: formGroup.promotionCode.value || undefined,
            focusId: answer ? answer.id : defaultFocusId,
          },
          !participantProfile.profileId,
          false,
          searchParams
        )
      );
    }
  });

  const commonProps = {
    classes,
  };

  return (
    <div className={classes.containedor}>
      <form>
        <InputField
          label="First Name"
          name="firstName"
          onChange={handleFormChange}
          value={formGroup.firstName.value}
          className={classes.input}
          helperText={
            !formGroup.firstName.isValid &&
            formGroup.firstName.isDirty &&
            formGroup.firstName.error
          }
          error={!formGroup.firstName.isValid && formGroup.firstName.isDirty}
          variant="outlined"
          onBlur={resetValidation}
          {...commonProps}
        />
        <InputField
          label="Last Name"
          name="lastName"
          onChange={handleFormChange}
          value={formGroup.lastName.value}
          className={classes.input}
          helperText={
            !formGroup.lastName.isValid &&
            formGroup.lastName.isDirty &&
            formGroup.lastName.error
          }
          error={!formGroup.lastName.isValid && formGroup.lastName.isDirty}
          onBlur={resetValidation}
          variant="outlined"
          {...commonProps}
        />
        <InputField
          label="Email Address"
          onChange={handleFormChange}
          name="emailAddress"
          type="email"
          value={formGroup.emailAddress.value}
          variant="outlined"
          className={classes.input}
          helperText={
            !formGroup.emailAddress.isValid &&
            formGroup.emailAddress.isDirty &&
            formGroup.emailAddress.error
          }
          error={
            !formGroup.emailAddress.isValid && formGroup.emailAddress.isDirty
          }
          onBlur={resetValidation}
          {...commonProps}
        />
        <PasswordField
          label="Password"
          onChange={handleFormChange}
          name="password"
          value={formGroup.password.value}
          helperText={
            !formGroup.password.isValid &&
            formGroup.password.isDirty &&
            typeof formGroup.password.error === "string" &&
            formGroup.password.error
          }
          error={!formGroup.password.isValid && formGroup.password.isDirty}
          isValid={
            formGroup.password.isValid &&
            !formGroup.password.error.length &&
            formGroup.password.value
          }
          onBlur={resetValidation}
          errors={formGroup.password.error}
          {...commonProps}
        />
        <div className={classes.promotionCode}>
          <a
            href="#promotionCode"
            onClick={handleTogglePromotion}
            className={classes.link}
          >
            I have an organization code{" "}
            {promotionCodeOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </a>
          {promotionCodeOpen ? (
            <InputField
              label=""
              required={false}
              id="promotionCode"
              name="promotionCode"
              value={formGroup.promotionCode.value}
              onChange={handleFormChange}
              {...commonProps}
            />
          ) : null}
        </div>
      </form>
    </div>
  );
};
